import { css, html, LitElement, nothing } from 'lit';
import { HasSlotController } from '../../common/slot-controller.js';
import { property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import type { TemplateResult } from 'lit';

/**
 * Shared building blocks for the component lab (`<component-app>`).
 *
 * The lab is organized as a list of {@link DemoSection}s grouped into
 * {@link DemoGroup}s. Each section renders one page: a short summary, a
 * sequence of `<demo-example>` cards and (when the section declares `tags`)
 * an auto-generated API reference sourced from `custom-elements.json`.
 */

/** One navigable page in the component lab. */
export interface DemoSection {
  /** Hash-route id: `#/button` renders the section with id 'button'. */
  id: string;
  /** Title shown in the sidebar and as the page heading. */
  title: string;
  /**
   * The custom element tag(s) this section documents. Used to render the
   * page's API reference tables from the custom elements manifest.
   */
  tags?: string[];
  /** One or two sentences describing the component, shown under the title. */
  summary?: string;
  /** The page body - typically a sequence of `<demo-example>` cards. */
  render: () => TemplateResult;
}

/** A labelled group of sections in the sidebar. */
export interface DemoGroup {
  label: string;
  sections: DemoSection[];
}

/** Removes the common leading indentation from a multi-line code snippet. */
export function stripIndent(source: string): string {
  const lines = source.replace(/^\n/, '').trimEnd().split('\n');
  const indents = lines.filter(line => line.trim().length > 0).map(line => /^ */.exec(line)![0].length);
  const trim = indents.length ? Math.min(...indents) : 0;
  return lines.map(line => line.slice(trim)).join('\n');
}

/**
 * Renders a `<demo-example>` card whose live demo IS the code snippet: the
 * markup string is rendered once and displayed once, so the demo can never
 * drift from its documentation. Only usable for plain-markup examples; demos
 * that need lit property/event bindings compose `<demo-example>` directly and
 * pass a hand-written snippet.
 */
export function codeExample(options: {
  label: string;
  description?: string | TemplateResult;
  code: string;
}): TemplateResult {
  const code = stripIndent(options.code);
  return html`
    <demo-example label=${options.label} .code=${code}>
      ${options.description ? html`<div slot="description">${options.description}</div>` : nothing} ${unsafeHTML(code)}
    </demo-example>
  `;
}

/**
 * Layout utilities for demo content. Slotted demo markup lives in the shadow
 * tree of whichever element renders it (the app shell or a stateful demo
 * element), so every one of those hosts includes this sheet.
 */
export const demoLayoutStyles = css`
  .demo-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--webmodule-spacing-small);
  }

  .demo-row + .demo-row,
  .demo-row + .demo-stack,
  .demo-stack + .demo-row,
  .demo-stack + .demo-stack,
  .demo-row + h4,
  .demo-stack + h4 {
    margin-top: var(--webmodule-spacing-large);
  }

  .demo-stack {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--webmodule-spacing-medium);
  }

  .demo-stack > * {
    align-self: stretch;
  }

  h4 {
    margin: 0 0 var(--webmodule-spacing-small) 0;
    font-size: var(--webmodule-font-size-small);
    font-weight: var(--webmodule-font-weight-semibold, 600);
    color: var(--webmodule-color-neutral-600);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  /* App-level pattern: place a control's label in a fixed-width column to its
     left by restyling the exported form-control parts. */
  .webmodule-control-left-label::part(form-control) {
    display: flex;
    flex-wrap: nowrap;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
  }

  .webmodule-control-left-label::part(form-control-label) {
    flex: 0 0 30%;
    max-width: 200px;
    padding-right: var(--webmodule-spacing-medium);
  }

  .webmodule-control-left-label::part(form-control-input) {
    flex: 1 1 100%;
  }

  .webmodule-control-left-label + .webmodule-control-left-label {
    margin-top: var(--webmodule-spacing-medium);
  }
`;

/**
 * A presentational card wrapping one example: a header with the example name
 * and a code toggle, an optional description, the live demo surface, and a
 * collapsible source snippet.
 */
export class DemoExample extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .example {
      background: var(--webmodule-panel-background-color);
      border: solid var(--webmodule-panel-border-width) var(--webmodule-panel-border-color);
      border-radius: var(--webmodule-border-radius-large);
      box-shadow: var(--webmodule-shadow-x-small);
      overflow: hidden;
    }

    header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--webmodule-spacing-medium);
      padding: var(--webmodule-spacing-medium) var(--webmodule-spacing-large);
      border-bottom: solid 1px var(--webmodule-color-neutral-100);
    }

    h3 {
      margin: 0;
      font-size: var(--webmodule-font-size-medium);
      font-weight: var(--webmodule-font-weight-semibold, 600);
      color: var(--webmodule-color-neutral-900);
    }

    .description {
      padding: var(--webmodule-spacing-medium) var(--webmodule-spacing-large) 0 var(--webmodule-spacing-large);
      color: var(--webmodule-color-neutral-700);
      font-size: var(--webmodule-font-size-small);
      line-height: var(--webmodule-line-height-normal);
    }

    .description ::slotted(*) {
      margin: 0;
    }

    .surface {
      padding: var(--webmodule-spacing-x-large) var(--webmodule-spacing-large);
    }

    pre {
      margin: 0;
      padding: var(--webmodule-spacing-large);
      border-top: solid 1px var(--webmodule-color-neutral-100);
      background: var(--webmodule-color-neutral-50);
      overflow-x: auto;
    }

    code {
      font-family: var(--webmodule-font-mono);
      font-size: var(--webmodule-font-size-x-small);
      line-height: 1.6;
      color: var(--webmodule-color-neutral-800);
      white-space: pre;
    }
  `;

  /** The example's display name. */
  @property() label = '';
  /** Source snippet shown by the Code toggle. When empty the toggle is hidden. */
  @property({ attribute: false }) code = '';
  @state() private codeVisible = false;
  private readonly hasSlotController = new HasSlotController(this, 'description');

  render() {
    const hasDescription = this.hasSlotController.checkFor('description');

    return html`
      <section class="example">
        <header>
          <h3>${this.label}</h3>
          ${this.code
            ? html`
                <webmodule-button
                  size="small"
                  variant=${this.codeVisible ? 'primary' : 'default'}
                  outline
                  @click=${() => (this.codeVisible = !this.codeVisible)}
                >
                  ${this.codeVisible ? 'Hide code' : 'Code'}
                </webmodule-button>
              `
            : nothing}
        </header>
        <div class="description" ?hidden=${!hasDescription}>
          <slot name="description"></slot>
        </div>
        <div class="surface">
          <slot></slot>
        </div>
        ${this.codeVisible ? html`<pre><code>${this.code}</code></pre>` : nothing}
      </section>
    `;
  }
}

/** Defines an internal demo element unless something already claimed the tag. */
export function defineDemoElement(tag: string, constructor: CustomElementConstructor) {
  if (!customElements.get(tag)) {
    customElements.define(tag, constructor);
  }
}
